import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Tooltip from '@src/components/Tooltip';
import {
  autoFormatTimeInput,
  formatNumericToSRTTime,
  shouldAutoFormat,
} from '@src/services/project';
import { DIGIT_SRT_TIME_REGEX } from '@src/constants/project';
import { StyledTimeFrameInput } from './TimeFrameInput.style';

const DEBOUNCE_DELAY = 200; // ms

const TimeFrameInput = ({
  value,
  onChange,
  onBlur,
  disabled = false,
  error = { hasError: false, message: '' },
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const [originalValue, setOriginalValue] = useState(value || '');
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const previousDigitCountRef = useRef(0);
  const localValueRef = useRef(value || '');
  localValueRef.current = localValue;

  useEffect(() => {
    // Sync from parent only when external value differs from current localValue.
    // This covers:
    //  - initial mount (no-op since localValue inits from value)
    //  - external resets (e.g. undo/redo reverts Redux while user is mid-typing)
    // Normal typing path: our own debounced dispatch echoes value back === localValue → no-op.
    if (value !== localValueRef.current) {
      setLocalValue(value || '');
      setOriginalValue(value || '');
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      setIsEditing(false);
    }
  }, [value]);

  // Handle error state - revert to original when error occurs and clear error
  useEffect(() => {
    if (error?.hasError && !isEditing) {
      // Revert to original value when there's an error
      setLocalValue(originalValue);
      // Call onChange with original value to clear the error
      onChange?.(originalValue);
    }
  }, [error?.hasError, isEditing, originalValue, onChange]);

  const handleClick = () => {
    if (disabled) return;
    setIsEditing(true);
    setOriginalValue(localValue);
    // Reset previous digit count when starting to edit
    previousDigitCountRef.current = localValue.replace(/\D/g, '').length;
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  // Debounced onChange call
  const debouncedOnChange = useCallback(
    (newValue) => {
      // Clear existing timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      // Set new timeout for debounced validation
      debounceRef.current = setTimeout(() => {
        onChange?.(newValue);
      }, DEBOUNCE_DELAY);
    },
    [onChange],
  );

  // Cleanup debounce on unmount
  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  const handleInputChange = (e) => {
    let newValue = e.target.value;
    const previousDigitCount = previousDigitCountRef.current;
    const previousDigitCountValue = localValue.replace(/\D/g, '').length;
    const currentDigitCount = newValue.replace(/\D/g, '').length;

    // If input is all digits and should be auto-formatted (paste operation or 9+ digits)
    if (shouldAutoFormat(newValue, previousDigitCount)) {
      newValue = formatNumericToSRTTime(newValue);
    } else {
      // Auto-format as user types (insert : and , automatically)
      // Only format if input contains digits and doesn't already have correct format
      const digitsOnly = newValue.replace(/\D/g, '');
      const hasCorrectFormat = DIGIT_SRT_TIME_REGEX.test(newValue);

      // If input has digits but not in correct format, apply auto-format
      if (digitsOnly.length > 0 && !hasCorrectFormat) {
        // Only auto-format when user is typing (digit count increased)
        // This prevents auto-formatting when user is deleting
        const isTyping = currentDigitCount > previousDigitCountValue;

        if (isTyping) {
          newValue = autoFormatTimeInput(newValue);
        }
      }
    }

    // Only allow digits, colon, and comma (time format: HH:mm:ss,SSS)
    if (/^[\d:,]*$/.test(newValue) && newValue.length <= 12) {
      setLocalValue(newValue);
      // Update previous digit count for next comparison
      previousDigitCountRef.current = newValue.replace(/\D/g, '').length;
      // Call debounced onChange for validation
      debouncedOnChange(newValue);
    }
  };

  const handleBlur = () => {
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    // Auto-format numeric input on blur if needed
    let finalValue = localValue;
    if (shouldAutoFormat(localValue, 0)) {
      finalValue = formatNumericToSRTTime(localValue);
      setLocalValue(finalValue);
    }

    setIsEditing(false);
    // Immediately call onChange on blur with the final value
    if (finalValue !== originalValue) {
      onChange?.(finalValue);
    }
    onBlur?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      // Clear any pending debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      // Revert to original value without calling onChange
      setLocalValue(originalValue);
      setIsEditing(false);
    }
  };

  return (
    <StyledTimeFrameInput
      isEditing={isEditing}
      hasError={error.hasError}
      disabled={disabled}
    >
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          maxLength={12}
        />
      ) : (
        <span
          onClick={handleClick}
          className="time-display"
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleClick();
            }
          }}
        >
          {localValue || '00:00:00,000'}
        </span>
      )}
      {error.hasError && (
        <Tooltip title={t(error.message)} placement="top" arrow>
          <InfoRoundedIcon className="error-icon" />
        </Tooltip>
      )}
    </StyledTimeFrameInput>
  );
};

export default TimeFrameInput;
